<script lang="ts" module>
  import type { StoreCartItem } from '../../stores/cart';

  export type CartItemProps = {
    /** Represents a cart item in the store. */
    item: StoreCartItem;
    /**
     * Callback function to remove an item from the cart.
     * @param sku - The SKU of the item to remove.
     */
    onRemove?: (sku: string) => void;
    /**
     * Callback function when the quantity input changes.
     * @param sku - The SKU of the item.
     * @param value - The new quantity value.
     */
    onInput?: (sku: string, value: number) => void;
    /**
     * Callback function to increase the quantity of an item.
     * @param sku - The SKU of the item to increase.
     */
    increase?: (sku: string) => void;
    /**
     * Callback function to decrease the quantity of an item.
     * @param sku - The SKU of the item to decrease.
     */
    decrease?: (sku: string) => void;
    /**
     * Whether the item is clickable.
     */
    clickable?: boolean;
  };
</script>

<script lang="ts">
  import { page } from '$app/state';

  import { XCircle } from 'phosphor-svelte';
  import { fade } from 'svelte/transition';

  import { hstack, stack, float, icon, visuallyHidden } from '@agapi/ui/panda/patterns';
  import { css } from '@agapi/ui/panda/css';
  import { Badge } from '@agapi/ui/badge';
  import { Loader } from '@agapi/ui/loader';
  import { QuantitySelector } from '@agapi/ui/quantity-selector';
  import { Image } from '@agapi/ui/image';
  import type { NavbarStore } from '@agapi/ui/navbar';
  import { getLocaleFormattedPrice, rewriteUrl } from '../../utils';

  import { cartItemStyles } from './cart-item.styles';
  import { getLocaleFromUrl } from '../../utils';
  import { calculatePercentDiscount } from '../../utils/pricing.server';

  import * as m from '$lib/parachute/messages';

  import { getContext } from 'svelte';

  const { item, onRemove, onInput, increase, decrease, clickable = false }: CartItemProps = $props();
  const classes = cartItemStyles.raw();

  let removing = $state(false);

  const navbarStore = getContext<NavbarStore>('navbar');

  const handleRemove = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    removing = true;
    onRemove?.(item.sku);
    return false;
  };



  const { originalPrice, totalPrice } = getLocaleFormattedPrice(item.price, getLocaleFromUrl(page.url));
  const discountLabel = item.price.discount ? calculatePercentDiscount(item.price) + '%' : undefined;
</script>

{#snippet ProductImage(item: StoreCartItem)}
  <Image
    src={item.imageUrl}
    alt={m.productImageAlt({ productName: item.name, brandName: item.brand })}
    background={`${item.imageUrl}?width=4`}
    width={300}
    aspectRatio={1}
    css={{
      objectFit: 'contain!',
      maxHeight: 'items.2xl',
      alignSelf: 'center'
    }}
  />
{/snippet}

{#snippet BrandName(item: StoreCartItem)}
  <p
    class={css({
      fontWeight: 'medium',
      fontSize: 'sm',
      color: 'text.vibrant',
      lineHeight: 'none'
    })}
  >
    {item.brand}
  </p>
{/snippet}

{#snippet ProductName(item: StoreCartItem)}
  <p class={css({ fontWeight: 'bold', fontSize: 'md', color: 'text.heading' })}>
    {item.name}
  </p>
{/snippet}

<li class={css(classes.item)} transition:fade>
  {#if removing}
    <span
      class={css({
        position: 'absolute',
        top: '50%',
        left: '60%',
        transform: 'translate(-50%, -50%)'
      })}
    >
      <Loader />
    </span>
  {/if}

  <div class={css(classes.container, { transition: 'opacity 0.3s', opacity: removing ? 0.5 : 1 })}>
    {#key item.sku}
      {#if clickable && item.handle}
        <a
          href={rewriteUrl(page, `/product/${item.handle}`)}
          onclick={() => {
            navbarStore.closeAll();
          }}
          class={css(classes.item, { flex: 1 })}
        >
          {@render ProductImage(item)}
        </a>
      {:else}
        {@render ProductImage(item)}
      {/if}
    {/key}
  </div>

  <div
    class={stack({
      gap: 'none',
      transition: 'opacity 0.3s',
      opacity: removing ? 0.5 : 1,
      justifyContent: 'space-between',
      flexGrow: '1'
    })}
  >
    <div class={stack({ position: 'relative', gap: '2' })}>
      {#if clickable && item.brandHandle}
        <a
          href={rewriteUrl(page, `/brand/${item.brandHandle}`)}
          onclick={() => {
            navbarStore.closeAll();
          }}
          class={css(classes.item, { flex: 1 })}
        >
          {@render BrandName(item)}
        </a>
      {:else}
        {@render BrandName(item)}
      {/if}

      {#if clickable && item.handle}
        <a
          href={rewriteUrl(page, `/product/${item.handle}`)}
          onclick={() => {
            navbarStore.closeAll();
          }}
          class={css(classes.item, { flex: 1 })}
        >
          {@render ProductName(item)}
        </a>
      {:else}
        {@render ProductName(item)}
      {/if}

      <button
        class={float({
          placement: 'top-end',
          p: '3',
          mt: '3',
          mr: '3',
          cursor: 'pointer'
        })}
        onclick={handleRemove}
        type="button"
      >
        <XCircle class={icon({ size: 'md', color: 'text.subdued' })}>
          <title>{m.iconRemove()}</title>
        </XCircle>
        <span class={visuallyHidden()}>{m.remove()}</span>
      </button>
    </div>

    <div
      class={hstack({
        justifyContent: 'space-between',
        alignItems: 'center',
        mt: '3',
        transition: 'opacity 0.3s',
        opacity: removing ? 0.5 : 1
      })}
    >
      <div class={stack({ justifyContent: 'space-between' })}>
        {#if item.variantName && item.variantName !== item.name}
          <p
            class={css({
              fontWeight: 'semibold',
              color: 'text.subdued',
              fontSize: 'sm',
              lineHeight: 'none'
            })}
          >
            {item.variantName}
          </p>
        {/if}

        {#key (item.sku, item.quantity)}
          <QuantitySelector
            size="xs"
            css={{ width: 'items.2xl', fontSize: 'md', zIndex: '5' }}
            minValue={1}
            maxValue={item.stock}
            value={item.quantity}
            onInput={(value: any) => onInput?.(item.sku, value)}
            handleIncrease={() => increase?.(item.sku)}
            handleDecrease={() => decrease?.(item.sku)}
          />
        {/key}
      </div>

      <div class={stack({ gap: '2', alignItems: 'flex-end' })}>
        {#if discountLabel}
          <Badge colorVariant="campaign" size="sm">
            {discountLabel}
          </Badge>
        {/if}

        <div class={hstack({ gap: '2', alignItems: 'center', justifyContent: 'center' })}>
          {#if originalPrice}
            <span
              class={css({
                textStyle: 'h6',
                fontFamily: 'body',
                fontWeight: '400',
                color: 'text.subdued',
                fontSize: 'md',
                lineHeight: 'none',
                textDecoration: 'line-through'
              })}
            >
              {originalPrice}
            </span>
          {/if}

          <span
            class={css({
              textStyle: 'h6',
              fontFamily: 'body',
              color: 'text.heading',
              fontSize: 'md',
              fontWeight: '900',
              lineHeight: 'none'
            })}
          >
            {totalPrice}
          </span>
        </div>
      </div>
    </div>
  </div>
</li>
